import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner, Alert, Badge, Button, Nav, Tab } from 'react-bootstrap';
import { ArrowLeft, MapPin, Mountain, Activity, Thermometer, CloudSnow, Check, Star, CalendarDays, Trash2 } from 'lucide-react';
import VentuskyWeather from './VentuskyWeather';
import PhotoCarousel from './PhotoCarousel';
import LogVisitModal from './LogVisitModal';
import { useAuth } from './AuthContext';
import useResortUserData from './useResortUserData';
import useVisits from './useVisits';

// webpackPrefetch hints the browser to idle-prefetch these chunks while the
// user sits on lighter pages (home, leaderboard). When they click into a
// resort, the chunks are already in cache → zero network wait.
const ResortTerrain3D = lazy(() => import(/* webpackPrefetch: true */ './ResortTerrain3D'));
const ClimateChart = lazy(() => import(/* webpackPrefetch: true */ './ClimateChart'));

const RatingSelector = ({ rating, onRate, hoverRating, setHoverRating }) => (
  <div className="rating-selector">
    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
      <button
        key={n}
        className={`rating-number${rating === n ? ' rating-number--selected' : ''}${hoverRating === n ? ' rating-number--hover' : ''}`}
        onClick={() => onRate(n)}
        onMouseEnter={() => setHoverRating(n)}
        onMouseLeave={() => setHoverRating(null)}
      >
        {n}
      </button>
    ))}
  </div>
);

const ResortDetail = () => {
  const { slug } = useParams();
  const [resortId, setResortId] = useState(null);
  const [resort, setResort] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasPhotos, setHasPhotos] = useState(null);
  const [hoverRating, setHoverRating] = useState(null);
  const [description, setDescription] = useState(null);
  const { user, signInWithGoogle } = useAuth();
  const { visited, rating, aggregateRating, toggleVisited, setRating } = useResortUserData(resortId, resort?.name);
  const { visits: resortVisits, addVisit, removeVisit } = useVisits(resortId);
  const [showLogVisit, setShowLogVisit] = useState(false);

  // Resolve slug to resort ID
  useEffect(() => {
    // If slug looks like a full ID (40-char hex), use it directly (backwards compat)
    if (/^[a-f0-9]{40}$/.test(slug)) {
      setResortId(slug);
      return;
    }
    // Otherwise look up the slug
    fetch('/data/slugs.json')
      .then(res => res.ok ? res.json() : {})
      .then(data => {
        const id = data.slugToId?.[slug];
        if (id) {
          setResortId(id);
        } else {
          setError('Resort not found');
          setLoading(false);
        }
      })
      .catch(() => {
        setError('Failed to load resort data');
        setLoading(false);
      });
  }, [slug]);

  useEffect(() => {
    if (!resortId) return;
    fetch('/data/descriptions.json')
      .then(res => res.ok ? res.json() : {})
      .then(data => setDescription(data[resortId] || null))
      .catch(() => {});
  }, [resortId]);

  useEffect(() => {
    if (!resortId) return;
    const fetchResortData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/resort/${resortId}`);

        if (!response.ok) {
          throw new Error(`Failed to fetch resort data: ${response.status}`);
        }

        const data = await response.json();
        setResort(data);
      } catch (error) {
        console.error('Error fetching resort data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchResortData();
  }, [resortId]);

  // Dynamic title and meta description
  useEffect(() => {
    if (!resort) return;
    const location = [resort.region, resort.country].filter(Boolean).join(', ');
    document.title = `${resort.name} — Snow & Weather | PowderMaps`;

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    const descText = description
      ? description.substring(0, 155) + (description.length > 155 ? '...' : '')
      : `Live snowfall data, weather forecasts, and resort details for ${resort.name}${location ? ` in ${location}` : ''}.`;
    metaDesc.setAttribute('content', descText);

    return () => {
      document.title = 'PowderMaps — Global Ski Resort Snow Tracker';
      metaDesc.setAttribute('content', 'Interactive ski resort weather map with snowfall forecasts');
    };
  }, [resort, description]);

  // Helper function to format snowfall amounts
  const formatSnowfall = (cm) => {
    if (cm === null || cm === undefined || isNaN(cm)) return '0.0 cm';
    return `${Number(cm).toFixed(1)} cm`;
  };

  // Helper function to format elevation
  const formatElevation = (meters) => {
    if (meters === null || meters === undefined) return 'N/A';
    return `${Number(meters).toLocaleString()} m`;
  };

  // Helper function to get graduated snow color
  const getSnowColor = (cm) => {
    if (cm === null || cm === undefined || cm === 0) return 'var(--pm-snow-zero)';
    if (cm < 10) return 'var(--pm-snow-light)';
    if (cm < 30) return 'var(--pm-snow-moderate)';
    return 'var(--pm-snow-heavy)';
  };

  const getSnowBadgeStyle = (cm) => ({
    backgroundColor: cm > 0 ? 'var(--pm-snow-badge-bg)' : 'var(--pm-bg-elevated)',
    color: getSnowColor(cm),
    border: `1px solid ${getSnowColor(cm)}`,
    fontFamily: 'var(--pm-font-data)',
    fontSize: '1.1rem',
  });

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <Spinner animation="border" role="status" size="lg" />
          <div className="mt-3">Loading resort details...</div>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Resort</Alert.Heading>
          <p>{error}</p>
          <Link to="/" className="btn btn-primary">
            <ArrowLeft className="me-2" size={16} />
            Back to Map
          </Link>
        </Alert>
      </Container>
    );
  }

  if (!resort) {
    return (
      <Container className="py-5">
        <Alert variant="warning">
          <Alert.Heading>Resort Not Found</Alert.Heading>
          <p>The requested resort could not be found.</p>
          <Link to="/" className="btn btn-primary">
            <ArrowLeft className="me-2" size={16} />
            Back to Map
          </Link>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="resort-detail-container" style={{ maxWidth: '1100px', paddingTop: '24px', paddingBottom: '48px' }}>
      {/* Back + header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <Link to="/" className="btn btn-outline-primary btn-sm">
          <ArrowLeft className="me-1" size={14} />
          Back to Map
        </Link>
      </div>

      <div className="mb-4">
        <h1 className="h2 fw-bold mb-1">{resort.name}</h1>
        <div className="d-flex align-items-center text-muted small">
          <MapPin size={14} className="me-1" style={{ flexShrink: 0 }} />
          <span>
            {resort.locality && `${resort.locality}, `}
            {resort.region && `${resort.region}, `}
            {resort.country}
            {resort.lat && resort.lng && (
              <span className="ms-2" style={{ opacity: 0.7 }}>
                ({Number(resort.lat).toFixed(4)}, {Number(resort.lng).toFixed(4)})
              </span>
            )}
          </span>
        </div>
      </div>

      <Tab.Container defaultActiveKey="overview">
        <Nav variant="pills" className="mb-3 resort-detail-tabs">
          <Nav.Item>
            <Nav.Link eventKey="overview">Overview</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="terrain">
              <Mountain size={14} className="me-1" style={{ verticalAlign: 'text-bottom' }} />
              3D Map
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
        <Tab.Pane eventKey="overview">
          <Row className="g-4">
            <Col lg={9}>

      {/* Photos (if available) */}
      {hasPhotos !== false && (
        <Card id="photos" className="resort-detail-section border-0 shadow-sm mb-4 overflow-hidden">
          <Card.Body className="p-0">
            <PhotoCarousel
              resortId={resort.id}
              resortName={resort.name}
              onPhotosDiscovered={setHasPhotos}
            />
          </Card.Body>
        </Card>
      )}

      {/* Rating & Visited */}
      <Card id="rating" className="resort-detail-section border-0 shadow-sm mb-4">
        <Card.Body className="p-3">
          <Row className="g-3 align-items-center">
            {/* Community Rating */}
            <Col xs={12} md={5}>
              <div className="text-center">
                {aggregateRating ? (
                  <>
                    <div style={{ fontSize: '2.2rem', fontWeight: 700, color: 'var(--pm-accent)', lineHeight: 1.1 }}>
                      {aggregateRating.average}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--pm-text-muted)' }}>
                      avg from {aggregateRating.count} {aggregateRating.count === 1 ? 'rating' : 'ratings'}
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: '0.85rem', color: 'var(--pm-text-muted)' }}>
                    No ratings yet
                  </div>
                )}
              </div>
            </Col>

            {/* Divider */}
            <Col xs={12} md={1} className="d-none d-md-flex justify-content-center">
              <div style={{ width: '1px', height: '60px', background: 'var(--pm-border)' }} />
            </Col>
            <hr className="d-md-none my-0" style={{ borderColor: 'var(--pm-border)' }} />

            {/* Your Rating */}
            <Col xs={12} md={6}>
              {!user ? (
                <div className="text-center">
                  <p style={{ fontSize: '0.85rem', color: 'var(--pm-text-muted)', margin: '0 0 8px' }}>
                    Sign in to rate this resort
                  </p>
                  <Button variant="outline-primary" size="sm" onClick={signInWithGoogle}>
                    Sign In with Google
                  </Button>
                </div>
              ) : (
                <div className="text-center">
                  <div className="d-flex gap-2 justify-content-center mb-2">
                    <Button
                      variant={visited ? 'primary' : 'outline-primary'}
                      size="sm"
                      onClick={toggleVisited}
                      style={{ borderRadius: '20px', padding: '4px 16px' }}
                    >
                      {visited && <Check size={14} className="me-1" />}
                      {visited ? 'Visited' : 'Mark as Visited'}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setShowLogVisit(true)}
                      style={{ borderRadius: '20px', padding: '4px 16px' }}
                    >
                      <CalendarDays size={14} className="me-1" />
                      Log Visit
                    </Button>
                  </div>
                  {visited && (
                    <div>
                      <small className="d-block mb-1" style={{ color: 'var(--pm-text-muted)', fontSize: '0.75rem' }}>
                        {rating ? `Your rating: ${rating}/10` : 'Rate this resort'}
                      </small>
                      <RatingSelector
                        rating={rating}
                        onRate={setRating}
                        hoverRating={hoverRating}
                        setHoverRating={setHoverRating}
                      />
                    </div>
                  )}
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Visit History */}
      {user && resortVisits.length > 0 && (
        <Card id="visits" className="resort-detail-section border-0 shadow-sm mb-4" style={{ background: 'var(--pm-bg-surface)' }}>
          <Card.Body className="p-3">
            <h6 className="fw-bold mb-3" style={{ color: 'var(--pm-text)' }}>Your Visits</h6>
            <div className="d-flex flex-column gap-2">
              {resortVisits.map(visit => (
                <div key={visit.id} className="visit-list-item">
                  <div>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--pm-text)' }}>
                        {visit.date?.toDate ? visit.date.toDate().toLocaleDateString() : new Date(visit.date).toLocaleDateString()}
                      </span>
                      <span className="visit-list-stars">
                        {[1,2,3,4,5].map(n => (
                          <Star key={n} size={12} fill={visit.snowRating >= n ? 'var(--pm-accent)' : 'none'} stroke={visit.snowRating >= n ? 'var(--pm-accent)' : 'var(--pm-text-muted)'} />
                        ))}
                      </span>
                    </div>
                    {visit.conditions?.length > 0 && (
                      <div className="d-flex gap-1 flex-wrap">
                        {visit.conditions.map(c => (
                          <span key={c} className="visit-condition-tag">{c}</span>
                        ))}
                      </div>
                    )}
                    {visit.notes && (
                      <div style={{ fontSize: '0.8rem', color: 'var(--pm-text-muted)', marginTop: '4px' }}>{visit.notes}</div>
                    )}
                  </div>
                  <button
                    className="visit-star-btn"
                    onClick={() => removeVisit(visit.id)}
                    title="Delete visit"
                  >
                    <Trash2 size={14} style={{ color: 'var(--pm-text-muted)' }} />
                  </button>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Snow Data — always two cards side by side */}
      <Row id="snow" className="resort-detail-section g-3 mb-4">
        <Col md={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="p-3">
              <h6 className="fw-bold mb-3">Recent Snowfall</h6>
              <Row className="g-2">
                {[
                  { label: '24h', value: resort.recentSnowfall?.snowfall24h },
                  { label: '48h', value: resort.recentSnowfall?.snowfall48h },
                  { label: '72h', value: resort.recentSnowfall?.snowfall72h },
                ].map(({ label, value }) => (
                  <Col xs={4} key={label}>
                    <div className="text-center p-2 rounded-2" style={{ background: 'var(--pm-bg-elevated)', borderBottom: `2px solid ${getSnowColor(value)}` }}>
                      <small className="d-block mb-1" style={{ color: 'var(--pm-text)' }}>{label}</small>
                      <Badge bg="" className="px-2 py-1" style={getSnowBadgeStyle(value)}>
                        {formatSnowfall(value)}
                      </Badge>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="p-3">
              <h6 className="fw-bold mb-3">Snow Forecast</h6>
              <Row className="g-2">
                {[
                  { label: '24h', value: resort.snowForecast?.next24Hours },
                  { label: '48h', value: resort.snowForecast?.next48Hours },
                  { label: '72h', value: resort.snowForecast?.next72Hours },
                ].map(({ label, value }) => (
                  <Col xs={4} key={label}>
                    <div className="text-center p-2 rounded-2" style={{ background: 'var(--pm-bg-elevated)', borderBottom: `2px solid ${getSnowColor(value)}` }}>
                      <small className="d-block mb-1" style={{ color: 'var(--pm-text)' }}>{label}</small>
                      <Badge bg="" className="px-2 py-1" style={getSnowBadgeStyle(value)}>
                        {formatSnowfall(value)}
                      </Badge>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Historical climate (decade-overlay weekly snowfall) */}
      {/* Temperature Forecast */}
      {resort.temperatureForecast && resort.temperatureForecast.length > 0 && (
        <Card id="forecast" className="resort-detail-section border-0 shadow-sm mb-4">
          <Card.Body className="p-3">
            <h6 className="fw-bold mb-3">
              <Thermometer size={16} className="me-1 text-primary" style={{ verticalAlign: 'text-bottom' }} />
              3-Day Forecast
            </h6>
            <Row className="g-2">
              {resort.temperatureForecast.map((day) => {
                const topMax = day.top?.max;
                const topMin = day.top?.min;
                const btmMax = day.bottom?.max;
                const btmMin = day.bottom?.min;
                const chance = day.chanceOfSnow;
                const hasData = topMax != null || btmMax != null;
                if (!hasData) return null;

                return (
                  <Col xs={4} key={day.day}>
                    <div className="p-2 rounded-2" style={{ background: 'var(--pm-bg-elevated)' }}>
                      <div className="text-center mb-2">
                        <small className="fw-bold" style={{ color: 'var(--pm-text)' }}>Day {day.day}</small>
                      </div>

                      {/* Summit temps */}
                      {topMax != null && (
                        <div className="text-center mb-2">
                          <small className="d-block" style={{ color: 'var(--pm-text-muted)', fontSize: '0.7rem' }}>Summit</small>
                          <div style={{ fontFamily: 'var(--pm-font-data)', fontSize: '0.85rem', color: 'var(--pm-text)' }}>
                            <span style={{ color: topMax <= 0 ? 'var(--pm-snow-moderate)' : 'var(--pm-rank-gold)' }}>{topMax}°</span>
                            <span style={{ color: 'var(--pm-text-dimmed)' }}> / </span>
                            <span style={{ color: topMin <= 0 ? 'var(--pm-snow-moderate)' : 'var(--pm-text-muted)' }}>{topMin}°</span>
                          </div>
                        </div>
                      )}

                      {/* Base temps */}
                      {btmMax != null && (
                        <div className="text-center mb-2">
                          <small className="d-block" style={{ color: 'var(--pm-text-muted)', fontSize: '0.7rem' }}>Base</small>
                          <div style={{ fontFamily: 'var(--pm-font-data)', fontSize: '0.85rem', color: 'var(--pm-text)' }}>
                            <span style={{ color: btmMax <= 0 ? 'var(--pm-snow-moderate)' : 'var(--pm-rank-gold)' }}>{btmMax}°</span>
                            <span style={{ color: 'var(--pm-text-dimmed)' }}> / </span>
                            <span style={{ color: btmMin <= 0 ? 'var(--pm-snow-moderate)' : 'var(--pm-text-muted)' }}>{btmMin}°</span>
                          </div>
                        </div>
                      )}

                      {/* Chance of snow */}
                      {chance != null && (
                        <div className="text-center pt-1" style={{ borderTop: '1px solid var(--pm-border)' }}>
                          <CloudSnow size={12} style={{ color: 'var(--pm-accent)', marginRight: '4px' }} />
                          <span style={{ fontFamily: 'var(--pm-font-data)', fontSize: '0.8rem', color: 'var(--pm-accent)' }}>
                            {Math.round(chance)}%
                          </span>
                        </div>
                      )}
                    </div>
                  </Col>
                );
              })}
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Resort Stats */}
      <Card id="details" className="resort-detail-section border-0 shadow-sm mb-4">
        <Card.Body className="p-3">
          <h6 className="fw-bold mb-3">Resort Details</h6>
          <Row className="g-2">
            <Col xs={6} sm={3}>
              <div className="text-center p-2 rounded-2" style={{ background: 'var(--pm-bg-elevated)' }}>
                <Mountain className="text-primary mb-1" size={18} />
                <small className="d-block" style={{ color: 'var(--pm-text)' }}>Vertical Drop</small>
                <strong className="small">{formatElevation(resort.verticalDrop)}</strong>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="text-center p-2 rounded-2" style={{ background: 'var(--pm-bg-elevated)' }}>
                <small className="d-block" style={{ color: 'var(--pm-text)' }}>Base</small>
                <strong className="small">{formatElevation(resort.minElevation)}</strong>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="text-center p-2 rounded-2" style={{ background: 'var(--pm-bg-elevated)' }}>
                <small className="d-block" style={{ color: 'var(--pm-text)' }}>Summit</small>
                <strong className="small">{formatElevation(resort.maxElevation)}</strong>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="text-center p-2 rounded-2" style={{ background: 'var(--pm-bg-elevated)' }}>
                <Activity className="text-primary mb-1" size={18} />
                <small className="d-block" style={{ color: 'var(--pm-text)' }}>Lifts</small>
                <strong className="small">{resort.liftCount || 'N/A'}</strong>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Description */}
      {description && (
        <Card id="about" className="resort-detail-section border-0 shadow-sm mb-4">
          <Card.Body className="p-3">
            <p style={{ color: 'var(--pm-text)', fontSize: '0.9rem', lineHeight: 1.65, margin: 0 }}>
              {description}
            </p>
          </Card.Body>
        </Card>
      )}

      {/* Historical climate (decade-overlay weekly snowfall) */}
      <Card id="climate" className="resort-detail-section border-0 shadow-sm mb-4">
        <Card.Body className="p-3">
          <Suspense fallback={<div className="text-muted small">Loading climate data…</div>}>
            <ClimateChart resortId={resort.id} resortName={resort.name} />
          </Suspense>
        </Card.Body>
      </Card>

      {/* Weather Forecast */}
      <div id="weather" className="resort-detail-section mb-4">
        <VentuskyWeather
          lat={resort.lat}
          lng={resort.lng}
          resortName={resort.name}
        />
      </div>

            </Col>
            <Col lg={3} className="d-none d-lg-block">
              <nav className="resort-toc">
                <div className="resort-toc-title">On this page</div>
                <ul className="resort-toc-list">
                  {hasPhotos !== false && <li><a href="#photos">Photos</a></li>}
                  <li><a href="#rating">Rating</a></li>
                  {user && resortVisits.length > 0 && <li><a href="#visits">Your Visits</a></li>}
                  <li><a href="#snow">Snow Data</a></li>
                  {resort.temperatureForecast && resort.temperatureForecast.length > 0 && (
                    <li><a href="#forecast">3-Day Forecast</a></li>
                  )}
                  <li><a href="#details">Resort Details</a></li>
                  {description && <li><a href="#about">About</a></li>}
                  <li><a href="#climate">Climate History</a></li>
                  <li><a href="#weather">Weather Map</a></li>
                </ul>
              </nav>
            </Col>
          </Row>
        </Tab.Pane>

        <Tab.Pane eventKey="terrain" mountOnEnter>
          <Card className="border-0 shadow-sm mb-4 overflow-hidden">
            <Card.Body className="p-0">
              <Suspense fallback={
                <div className="terrain3d-fallback">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading 3D map…
                </div>
              }>
                <ResortTerrain3D
                  resortId={resortId}
                  lat={resort.lat}
                  lng={resort.lng}
                  name={resort.name}
                  minElevation={resort.minElevation}
                  maxElevation={resort.maxElevation}
                />
              </Suspense>
            </Card.Body>
          </Card>
        </Tab.Pane>

        </Tab.Content>
      </Tab.Container>

      {/* Log Visit Modal */}
      <LogVisitModal
        show={showLogVisit}
        onHide={() => setShowLogVisit(false)}
        onSave={addVisit}
        resort={resort ? { id: resortId, name: resort.name } : null}
      />
    </Container>
  );
};

export default ResortDetail;